import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";

import ErrorIcon from '@mui/icons-material/Error';

import {
    reportAvailableFetch,
    reportCreateFetch,
    reportListFetch,
    reportStatusFetch,
    reportSetSelected
} from "../../../../redux/report/reportActions";

import {
    styled,
    InputLabel,
    MenuItem,
    FormHelperText,
    FormControl,
    Select,
    Button,
    LinearProgress,
    Typography,
} from '@mui/material';

const PREFIX = 'CreateReport';

const classes = {
    root: `${PREFIX}-root`,
    formControl: `${PREFIX}-formControl`,
    button: `${PREFIX}-button`,
    selectEmpty: `${PREFIX}-selectEmpty`,
    linearProgress: `${PREFIX}-linearProgress`,
    reportDescription: `${PREFIX}-reportDescription`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        width: '100%',
        height: '100%',
        textAlign: 'right',
        overflow: 'auto'
    },

    [`& .${classes.formControl}`]: {
        margin: '8px',
        width: 'calc(100% - 16px)'
    },

    [`& .${classes.button}`]: {
        marginRight: '8px'
    },

    [`& .${classes.selectEmpty}`]: {
        marginTop: theme.spacing(2),
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.reportDescription}`]: {
        marginTop: '8px'
    }
}));

export default function CreateReport() {

    const dispatch = useDispatch();

    // Component State
    const [reportItems, setReportItems] = useState(null);
    const [wbsgid, setWBSGID] = useState(null);
    const [createDisabled, setCreateDisabled] = useState(true);

    // App State
    const availableReports = useSelector(state => state.report.availableReports);
    const filters = useSelector(state => state.data.filters);
    const reportSelected = useSelector(state => state.report.reportSelected);
    const reportStatus = useSelector(state => state.report.reportStatus);

    // wbsgid Filter?? Hard-coded dependency
    const filter = filters.find(f => f.field === 'wbsgid');
    const condition = filter ? filter.conditions[0] : null;

    useEffect(() => {

        if (!condition)
        {
            return;
        }

        const conditions = filter.conditions[0];
        const parts = conditions.split(' ');
        let wbsgid = parts[1];

        if (!isNaN(wbsgid)) {
            setWBSGID(wbsgid);
        }
        else {
            setWBSGID(null);
        }
    }, [condition])

    useEffect(() => {
        dispatch(reportAvailableFetch(wbsgid));
    }, [wbsgid]);

    useEffect(() => {
        if (!availableReports.data || availableReports.data.length === 0){
            setReportItems(null);
            return;
        }

        let menuItems = [];

        // Reset the selected index
        let selectedIndex = -2112;
        let selectedLabel = null;
        let selectedName = null;
        let selectedDescription = null;

        menuItems.push(<MenuItem value={-2112}>All Reports</MenuItem>);

        for (let r = 0; r < availableReports.data.length; r++) {
            // Previously selected??
            if (availableReports.data[r].Label === reportSelected.label) {
                selectedIndex = r;
                selectedLabel = availableReports.data[r].Label;
                selectedName = availableReports.data[r].Name;
                selectedDescription = availableReports.data[r].Description;
            }
            menuItems.push(<MenuItem value={r}>{availableReports.data[r].Label}</MenuItem>);
        }

        dispatch(reportSetSelected({index: selectedIndex, label: selectedLabel, name: selectedName, description: selectedDescription}));
        setReportItems(menuItems);

    }, [availableReports]);

    useEffect(() => {
        if (reportSelected.index < 0 || !wbsgid ) {
            enableCreate();
        } else {
            // Get report status... we're limiting to one report
            // per user at a time
            dispatch(reportStatusFetch(wbsgid, reportSelected.Name));
        }
    }, [reportSelected])

    useEffect(() => {

        enableCreate();

    }, [reportStatus]);

    const enableCreate = () =>  {
        let enable = false;

        if (reportSelected.index >= 0) {
            if (reportStatus.data && reportStatus.data.length === 0) {
                enable = true;
            } else if (reportStatus.data) {
                const status = reportStatus.data[0];
                // Complete or Error
                if (status.Status.toLowerCase() !== 'in progress ...') {
                    enable = true;
                } else {
                    // Get report status... we're limiting to one report
                    // per user at a time
                    if (window.reportStatusHandle) {
                        clearTimeout(window.reportStatusHandle);
                    }
                    window.reportStatusHandle = setTimeout(() => {
                        dispatch(reportStatusFetch(wbsgid, reportSelected.name));
                    }, 5000);
                }
            }
        }

        setCreateDisabled(!enable);
    };

    const handleReportChange = (event) => {
        if (event.target.value === -2112)
            dispatch(reportSetSelected({index: event.target.value, label: "", name: "", description: ""}));
        else
            dispatch(reportSetSelected({index: event.target.value, label: availableReports.data[event.target.value].Label, name: availableReports.data[event.target.value].Name, description: availableReports.data[event.target.value].Description}));
    };

    const handleCreateReport = (event) => {
        // Disable button
        setCreateDisabled(true);

        // Create report
        dispatch(reportCreateFetch(wbsgid, reportSelected.name, reportSelected.label));

        // After 5 Seconds, the time it should take to add something to the execution log, check status
        if (window.reportStatusHandle) {
            clearTimeout(window.reportStatusHandle);
        }
        window.reportStatusHandle = setTimeout(() => {
            dispatch(reportStatusFetch(wbsgid, reportSelected.name));
        }, 5000);
    };

    return (
        <Root className={classes.root}>
			{!availableReports.fetched &&
				<LinearProgress className={classes.linearProgress}/>
			}
			{availableReports.fetched &&
				<div className={classes.linearProgress}/>
			}
			<FormControl className={classes.formControl} variant="standard">
				<InputLabel id="label">Available Reports</InputLabel>
				<Select
					labelId="label"
					id="select"
					value={reportSelected.index}
					onChange={handleReportChange}
				>
					{reportItems}
				</Select>
				{reportSelected.index >= 0 &&
					<Typography className={classes.reportDescription} variant="caption" display="block" gutterBottom>{reportSelected.description}</Typography>
				}
				{reportSelected.index === -2112 &&
					<Typography className={classes.reportDescription} variant="caption" display="block" gutterBottom>Select report...</Typography>
				}
				{reportStatus && reportStatus.data?.length > 0 && reportStatus.data[0].ErrorMessage &&
					<div style={{display: 'inline-flex'}}>
						<ErrorIcon style={{marginRight: '8px'}}/>
						<Typography className={classes.reportDescription} variant="caption" display="block" gutterBottom>Last Attempt: {reportStatus.data[0].CreatedOn}</Typography>
						<Typography className={classes.reportDescription} variant="caption" display="block" gutterBottom> Error: {reportStatus.data[0].ErrorMessage}</Typography>
					</div>
				}
			</FormControl>
			<Button variant="contained" color="primary" disabled={createDisabled} onClick={handleCreateReport} className={classes.button}>
				Create Report
			</Button>
        </Root>
    );
}
